import { TrendingUp, TrendingDown, ChevronRight } from 'lucide-react';
import { StatusBadge } from '@components/ui';
import clsx from 'clsx';

const formatCurrency = (amount) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });

const TYPE_VARIANT = {
    supplier: 'info',
    subcontractor: 'warning',
    service: 'secondary',
};

function CompanyInitial({ name }) {
    const initial = (name || '?').charAt(0).toUpperCase();
    return (
        <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary font-bold text-sm flex items-center justify-center shrink-0">
            {initial}
        </div>
    );
}

export default function CariHesaplarTablosu({ data, onRowClick }) {
    if (!data || data.length === 0) {
        return (
            <div className="py-16 text-center text-text-tertiary">
                <p>Kayıt bulunamadı.</p>
            </div>
        );
    }

    return (
        <>
            {/* Mobile Card View */}
            <div className="md:hidden space-y-3 px-4 pb-4">
                {data.map(account => (
                    <button
                        key={account.id}
                        onClick={() => onRowClick(account)}
                        className="w-full text-left bg-bg-secondary rounded-xl p-4 hover:bg-bg-hover transition-colors"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <CompanyInitial name={account.companyName} />
                                <div className="min-w-0">
                                    <p className="font-semibold text-text-primary truncate">{account.companyName}</p>
                                    <StatusBadge
                                        status={account.companyTypeLabel}
                                        variant={TYPE_VARIANT[account.companyType] || 'secondary'}
                                        size="xs"
                                    />
                                </div>
                            </div>
                            <ChevronRight size={16} className="text-text-muted shrink-0 mt-1" />
                        </div>

                        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border-light">
                            <div>
                                <p className="text-xs text-text-tertiary">Toplam Alış</p>
                                <p className="text-sm font-medium text-error">{formatCurrency(account.totalPurchases)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-text-tertiary">Toplam Ödeme</p>
                                <p className="text-sm font-medium text-success">{formatCurrency(account.totalPayments)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-text-tertiary">Bakiye</p>
                                <p className={clsx(
                                    'text-sm font-bold',
                                    account.currentBalance > 0 ? 'text-error' : 'text-success'
                                )}>
                                    {formatCurrency(account.currentBalance)}
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-text-muted mt-2">
                            Son hareket: {formatDate(account.lastMovementDate)} • {account.movementCount} işlem
                        </p>
                    </button>
                ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border-light bg-bg-secondary">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Firma</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Tür</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Toplam Alış</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Toplam Ödeme</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Bakiye</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">İşlem</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-text-tertiary uppercase tracking-wider">Son Hareket</th>
                            <th className="w-8"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light">
                        {data.map(account => {
                            const isPaid = account.currentBalance === 0;
                            return (
                                <tr
                                    key={account.id}
                                    onClick={() => onRowClick(account)}
                                    className="hover:bg-bg-hover cursor-pointer transition-colors group"
                                >
                                    {/* Firma */}
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <CompanyInitial name={account.companyName} />
                                            <div>
                                                <p className="font-semibold text-text-primary text-sm">{account.companyName}</p>
                                                <p className="text-xs text-text-muted truncate max-w-[160px]">{account.companyFullName}</p>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Tür */}
                                    <td className="px-4 py-3">
                                        <StatusBadge
                                            status={account.companyTypeLabel}
                                            variant={TYPE_VARIANT[account.companyType] || 'secondary'}
                                            size="xs"
                                        />
                                    </td>

                                    {/* Toplam Alış */}
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <TrendingUp size={13} className="text-error" />
                                            <span className="text-sm font-medium text-error">{formatCurrency(account.totalPurchases)}</span>
                                        </div>
                                    </td>

                                    {/* Toplam Ödeme */}
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                            <TrendingDown size={13} className="text-success" />
                                            <span className="text-sm font-medium text-success">{formatCurrency(account.totalPayments)}</span>
                                        </div>
                                    </td>

                                    {/* Bakiye */}
                                    <td className="px-4 py-3 text-right">
                                        <span className={clsx(
                                            'text-sm font-bold px-2 py-1 rounded-lg',
                                            isPaid
                                                ? 'bg-success/10 text-success'
                                                : 'bg-error/10 text-error'
                                        )}>
                                            {isPaid ? 'Kapalı' : formatCurrency(account.currentBalance)}
                                        </span>
                                    </td>

                                    {/* İşlem Sayısı */}
                                    <td className="px-4 py-3 text-center">
                                        <span className="text-sm text-text-secondary">{account.movementCount}</span>
                                    </td>

                                    {/* Son Hareket */}
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-text-tertiary">{formatDate(account.lastMovementDate)}</span>
                                    </td>

                                    {/* Arrow */}
                                    <td className="px-3 py-3">
                                        <ChevronRight size={16} className="text-text-muted group-hover:text-primary transition-colors" />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
